import {
  FullscreenDialogWithTitleFragment,
  FullscreenDialogWithTitleFragmentProps,
  FullscreenDialogWithTitleFragmentState
} from "../../shared/FullscreenDialogWithTitleFragment";
import {AnswerState, Lesson, LessonContext, LessonType} from "./types";
import {StyledBoxColumn, StyledBoxRow} from "../../shared/StyledComponents";
import {BORDER_RADIUS_MEDIUM, PAGE_FRAGMENT_XXLG_WIDTH, PD_LG, PD_SM} from "../../shared/dimens";
import {colorGreen, colorPastelGreen, colorPastelRed, colorRed, gray} from "../../shared/colors";
import React, {createRef, ReactElement} from "react";
import {Button, IconButton, Tooltip, Typography} from "@mui/material";
import {CloseOutlined} from "@mui/icons-material";
import {BaseLessonFragment, BaseRecordAudioLessonFragment} from "./BaseLessonFragment";
import {LESSONS} from "./consts";

export type LessonDialogFragmentProps = FullscreenDialogWithTitleFragmentProps & {
  lessonId: string,
}

type LessonDialogFragmentState = FullscreenDialogWithTitleFragmentState & {
  lesson: Lesson,
  pageIndex: number,
  selectedOptionIndex: number,
  answerState: AnswerState,
}

export class LessonDialogFragment extends FullscreenDialogWithTitleFragment<LessonDialogFragmentProps, LessonDialogFragmentState> implements LessonContext {

  protected onCreateState(): LessonDialogFragmentState {
    return {
      ...super.onCreateState(),
      lesson: LESSONS.find(lesson => lesson.id === this.props.lessonId),
      pageIndex: 0,
      selectedOptionIndex: null,
      answerState: AnswerState.NONE,
    };
  }

  componentDidUpdate(prevProps: Readonly<LessonDialogFragmentProps>, prevState: Readonly<LessonDialogFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.pageIndex !== this.state.pageIndex) {
      this.setSelectedOptionIndex(null);
      this.setState({
        answerState: AnswerState.NONE,
      })
    }
  }

  pageIndex(): number {
    return this.state.pageIndex;
  }

  nextPage(): void {
    this.setState({
      pageIndex: this.state.pageIndex + 1,
    });
  }

  setPageIndex(index: number) {
    this.setState({
      pageIndex: index,
    });
  }

  selectedOptionIndex(): number | null {
    return this.state.selectedOptionIndex;
  }

  setSelectedOptionIndex(optionIndex: number | null) {
    this.setState({
      selectedOptionIndex: optionIndex,
    });
  }

  answerState(): AnswerState {
    return this.state.answerState;
  }

  protected hideBackButton(): boolean {
    return true;
  }

  protected getContainerStyle(): any {
    return {
      paddingBottom: null,
    };
  }

  protected getCustomTitle(): React.ReactElement {
    const lesson = this.state.lesson;
    if (!lesson) {
      return null;
    }
    const renderClass = lesson.renderClass;
    const progress = (this.state.pageIndex * 100) / (renderClass.totalPages() >= 1 ? renderClass.totalPages() : 1);
    return <StyledBoxRow style={{flexGrow: 1, padding: PD_SM, alignItems: "center", gap: PD_LG, pointerEvents: "auto"}}>
      <Tooltip title={"Close"}>
        <IconButton onClick={() => this.onGoBack()}>
          <CloseOutlined/>
        </IconButton>
      </Tooltip>
      <div style={{flexGrow: 1, position: "relative", height: 16}}>
        <div style={{
          width: "100%",
          background: gray,
          borderRadius: BORDER_RADIUS_MEDIUM,
          height: "100%",
          position: "absolute"
        }}/>
        <div style={{
          width: progress + "%",
          background: this.theme.palette.secondary.main,
          borderRadius: BORDER_RADIUS_MEDIUM, height: "100%",
          position: "absolute",
        }}/>
      </div>
    </StyledBoxRow>;
  }

  private readonly renderRef = createRef<BaseLessonFragment<any, any>>();

  renderContent() {
    const lesson = this.state.lesson;
    if (!lesson) {
      return null;
    }
    const RenderClass = lesson.renderClass;
    return <StyledBoxColumn style={{flexGrow: 1, maxWidth: PAGE_FRAGMENT_XXLG_WIDTH, width: "100%", margin: "auto"}}>
      <RenderClass ref={this.renderRef} path={this.props.path} pageIndex={this.state.pageIndex} lesson={lesson} context={this}/>
      {this.renderActionButton(lesson)}
    </StyledBoxColumn>;
  }

  private renderActionButton(lesson: Lesson): ReactElement {
    switch (lesson.renderClass.lessonType()) {
      case LessonType.FLIP:
        return this.renderActionButtonFlip();
      case LessonType.RECORD_AUDIO:
        return this.renderActionButtonSingleChoice();
    }
    return null;
  }

  private renderActionButtonFlip(): ReactElement {
    return <StyledBoxColumn style={{padding: PD_LG}}>
      <Button
        onClick={() => this.nextPage()}
        variant="contained"
        style={{
          padding: PD_LG,
        }}>Continue</Button>
    </StyledBoxColumn>;
  }

  private renderActionButtonSingleChoice(): ReactElement {
    let backgroundColor: string;
    if (this.state.answerState === AnswerState.CORRECT) {
      backgroundColor = colorPastelGreen;
    } else if (this.state.answerState === AnswerState.INCORRECT) {
      backgroundColor = colorPastelRed;
    }
    const lessonFragment = this.renderRef.current as BaseRecordAudioLessonFragment<any, any>;
    return <StyledBoxColumn style={{padding: PD_LG, backgroundColor: backgroundColor}}>
      <StyledBoxColumn style={{height: 72, alignItems: "center"}}>
        {this.state.answerState === AnswerState.CORRECT
          ? <>
            <Typography variant="h6">Correct!</Typography>
            <Typography>That's right.</Typography>
          </>
          : null}
        {this.state.answerState === AnswerState.INCORRECT
          ? <>
            <Typography variant="h6">Correct answer</Typography>
            <Typography>{lessonFragment.correctAnswer()}</Typography>
          </>
          : null}
      </StyledBoxColumn>
      {this.state.answerState === AnswerState.NONE
        ? <Button
          onClick={() => this.setState({
            answerState: Boolean(lessonFragment.checkSelectedOption()) ? AnswerState.CORRECT : AnswerState.INCORRECT,
          })}
          disabled={this.state.selectedOptionIndex === null}
          variant="contained"
          style={{padding: PD_LG}}>Check</Button>
        : null}
      {this.state.answerState === AnswerState.CORRECT || this.state.answerState === AnswerState.INCORRECT
        ? <Button
          onClick={() => this.nextPage()}
          variant="contained"
          style={{
            padding: PD_LG,
            backgroundColor: this.state.answerState === AnswerState.CORRECT ? colorGreen : colorRed
          }}>Continue</Button>
        : null}
    </StyledBoxColumn>;

  }
}